import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import {
  useDataModel, params, string, number, shape, arrayOf, bool, extend, customType
} from '@thd-nucleus/data-sources';
import { LoadingPlaceholder } from '@thd-olt-component-react/loading-placeholder';
import { Promotion } from '@thd-olt-component-react/promotion';
import PriceSimple from './partials/PriceSimple';
import PriceDetailed from './partials/PriceDetailed';
import { PreferredPricingBadge } from './partials/PreferredPricingBadge';
import Sku from '../models/Sku';
import '../styles/price-global.style.scss';

const Price = (props) => {
  const { channel, isServer } = useContext(ExperienceContext);
  const { storeId: defaultStoreId, storeZip: defaultStoreZip } = useStore();
  const {
    basic,
    disableRangePricing,
    displayEachUom,
    hideBadge,
    hideBulkPrice,
    hideConditionalPromoDescription,
    hideLimitPerOrder,
    hidePromotions,
    hideSavingsText,
    hideRebates,
    itemId,
    large,
    omitDetails,
    onSavingsCenterToggle,
    showPreferredPricingBadge,
    showPreferredPricingBadgeToolTip,
    /* eslint-disable-next-line react/prop-types */
    product,
    skip,
    storeId = defaultStoreId,
    zipCode = defaultStoreZip
  } = props;

  const options = {
    ssr: !skip,
    skip: !!product,
    variables: {
      itemId,
      storeId
    }
  };

  const [sku, setSku] = useState(null);

  const { data, loading } = useDataModel('product', options);

  const productToBeUsed = product || data?.product;
  useEffect(() => { if (productToBeUsed) setSku(new Sku(productToBeUsed)); }, [productToBeUsed]);

  if (skip && isServer) return null;

  if (loading) {
    return (
      <LoadingPlaceholder
        type="text"
        ready={false}
        color="#E0E0E0"
        rows={1}
        showLoadingAnimation
      >
        Loading...
      </LoadingPlaceholder>
    );
  }

  if (!productToBeUsed || !sku) {
    return null;
  }

  const preferredPriceFlag = sku.preferredPriceFlag;

  return (
    <>
      { preferredPriceFlag && showPreferredPricingBadge && (
        <PreferredPricingBadge
          channel={channel}
          large={large}
          showPreferredPricingBadgeToolTip={showPreferredPricingBadgeToolTip}
        />
      )}
      {large && (
        <PriceDetailed
          channel={channel}
          disableRangePricing={disableRangePricing}
          displayEachUom={displayEachUom}
          hideBadge={hideBadge}
          hideBulkPrice={hideBulkPrice}
          hideConditionalPromoDescription={hideConditionalPromoDescription}
          hideLimitPerOrder={hideLimitPerOrder}
          hidePromotions={hidePromotions}
          hideSavingsText={hideSavingsText}
          hideRebates={hideRebates}
          omitDetails={omitDetails}
          product={sku}
          storeId={storeId}
          zipCode={zipCode}
        />
      )}
      {!large && (
        <PriceSimple
          basic={basic}
          channel={channel}
          disableRangePricing={disableRangePricing}
          displayEachUom={displayEachUom}
          hideBadge={hideBadge}
          hideBulkPrice={hideBulkPrice}
          hideLimitPerOrder={hideLimitPerOrder}
          hideSavingsText={hideSavingsText}
          hideRebates={hideRebates}
          omitDetails={omitDetails}
          onSavingsCenterToggle={onSavingsCenterToggle}
          product={sku}
          zipCode={zipCode}
        />
      )}
    </>
  );
};

Price.propTypes = {
  basic: PropTypes.bool,
  disableRangePricing: PropTypes.bool,
  displayEachUom: PropTypes.bool,
  hideBadge: PropTypes.bool,
  hideBulkPrice: PropTypes.bool,
  hideConditionalPromoDescription: PropTypes.bool,
  hideLimitPerOrder: PropTypes.bool,
  hidePromotions: PropTypes.bool,
  /** Hide Savings Text */
  hideSavingsText: PropTypes.bool,
  /** Hide Rebate Messaging and prevent Rebate Calls */
  hideRebates: PropTypes.bool,
  itemId: PropTypes.string.isRequired,
  /** Determines whether to display larger or smaller Price Component Variant */
  large: PropTypes.bool,
  omitDetails: PropTypes.bool,
  onSavingsCenterToggle: PropTypes.func,
  showPreferredPricingBadge: PropTypes.bool,
  showPreferredPricingBadgeToolTip: PropTypes.bool,
  skip: PropTypes.bool,
  /** Store Id for Pricing Query */
  storeId: PropTypes.string,
  zipCode: PropTypes.string
};

Price.defaultProps = {
  basic: false,
  disableRangePricing: false,
  displayEachUom: false,
  hideBadge: false,
  hideBulkPrice: false,
  hideConditionalPromoDescription: false,
  hideLimitPerOrder: false,
  hidePromotions: false,
  hideSavingsText: false,
  hideRebates: false,
  large: true,
  omitDetails: false,
  onSavingsCenterToggle: () => {},
  showPreferredPricingBadge: false,
  showPreferredPricingBadgeToolTip: false,
  skip: false,
  storeId: undefined,
  zipCode: undefined
};

Price.dataModel = extend({
  product: params({
    itemId: string().isRequired(),
    dataSource: string(),
    loyaltyMembershipInput: customType('LoyaltyMembershipInput').shape({
      svocID: string(),
      programTiers: arrayOf(shape({
        tier: string(),
        program: string()
      }))
    })
  }).shape({
    itemId: string(),
    dataSources: string(),
    identifiers: shape({
      itemId: string()
    }),
    info: shape({
      hidePrice: bool(),
      ecoRebate: bool(),
      quantityLimit: number(),
      sskMin: string(),
      sskMax: string(),
      unitOfMeasureCoverage: string(),
      wasMaxPriceRange: number({ float: true }),
      wasMinPriceRange: number({ float: true })
    }),
    media: shape({
      images: arrayOf(shape({
        url: string(),
        type: string(),
        subType: string(),
        sizes: arrayOf(string())
      }))
    }),
    pricing: params({ storeId: string() }).shape({
      alternatePriceDisplay: bool(),
      alternate: shape({
        bulk: shape({
          pricePerUnit: number({ float: true }),
          thresholdQuantity: number(),
          value: number({ float: true })
        }),
        unit: {
          caseUnitOfMeasure: string(),
          unitsOriginalPrice: number({ float: true }),
          unitsPerCase: number({ float: true }),
          value: number({ float: true }),
        },
      }),
      original: number({ float: true }),
      mapAboveOriginalPrice: bool(),
      message: string(),
      preferredPriceFlag: bool(),
      promotion: shape({
        type: string(),
        description: shape({
          shortDesc: string(),
          longDesc: string()
        }),
        dollarOff: number({ float: true }),
        percentageOff: number({ float: true }),
        savingsCenter: string(),
        savingsCenterPromos: string(),
        specialBuySavings: string(),
        specialBuyDollarOff: number({ float: true }),
        specialBuyPercentageOff: number({ float: true })
      }),
      specialBuy: number({ float: true }),
      unitOfMeasure: string(),
      value: number({ float: true }),
    }),
  })
},
Promotion
);

export default Price;
